import React, { useCallback, useEffect, useState } from 'react';
import { Confirm } from 'semantic-ui-react';
import { getUsers, updateUser } from '../../../services/user';
import { EstateUnit } from '../../../types/EstateUnit';
import { UserState } from '../../../types/UserState';
import {
  dangerNotification,
  successNotification
} from '../../../services/notification';
import AssignUserModal from './AssignUserModal';
import UsersCard from './users/UsersCard';

type EstateUnitUsersPanelProps = {
  selectedUnit: EstateUnit | null;
  estateId: string;
  searchId: string;
  canManage: boolean;
  refreshKey?: number;
};

const EstateUnitUsersPanel: React.FC<EstateUnitUsersPanelProps> = ({
  selectedUnit,
  estateId,
  searchId,
  canManage,
  refreshKey = 0
}) => {
  const [users, setUsers] = useState<UserState[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [userToUnassign, setUserToUnassign] = useState<UserState | null>(null);
  const [isUnassigning, setIsUnassigning] = useState(false);

  const loadUsers = useCallback(async (page = currentPage) => {
    if (!selectedUnit?.id || !searchId || !estateId) {
      setUsers([]);
      setTotalCount(0);
      return;
    }

    setIsLoading(true);

    try {
      const response = await getUsers(searchId, page, {
        estate: estateId,
        estateUnitId: selectedUnit.id
      });
      setUsers(response.data || []);
      if (page === 1) {
        setTotalCount(response.count || 0);
      }
    } catch {
      dangerNotification('Oops...', 'Não foi possível carregar os usuários da unidade.');
      setUsers([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [selectedUnit?.id, searchId, estateId, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedUnit?.id, estateId, refreshKey]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers, refreshKey]);

  const handleUnassign = async () => {
    if (!userToUnassign) {
      return;
    }

    setIsUnassigning(true);

    try {
      await updateUser(userToUnassign.id, {
        id: userToUnassign.id,
        estateUnitId: null,
        role: userToUnassign.role
      });
      successNotification('Sucesso', 'Usuário desvinculado da unidade.');
      setUserToUnassign(null);
      setCurrentPage(1);
      await loadUsers(1);
    } catch (error) {
      const message =
        (error as { message?: string })?.message ||
        'Não foi possível desvincular o usuário.';
      dangerNotification('Oops...', message);
    } finally {
      setIsUnassigning(false);
    }
  };

  return (
    <>
      <UsersCard
        selectedUnit={selectedUnit}
        users={users}
        isLoading={isLoading}
        canManage={canManage}
        isUnassigning={isUnassigning}
        totalCount={totalCount}
        currentPage={currentPage}
        onAssignClick={() => setIsAssignModalOpen(true)}
        onUnassignClick={setUserToUnassign}
        onPageChange={setCurrentPage}
      />

      {selectedUnit && (
        <AssignUserModal
          open={isAssignModalOpen}
          estateId={estateId}
          unitId={selectedUnit.id}
          searchId={searchId}
          onClose={() => setIsAssignModalOpen(false)}
          onSuccess={loadUsers}
        />
      )}

      <Confirm
        open={Boolean(userToUnassign)}
        content={`Deseja desvincular "${userToUnassign?.name}" desta unidade?`}
        cancelButton={{
          content: 'Cancelar',
          disabled: isUnassigning
        }}
        confirmButton={{
          content: 'Confirmar',
          loading: isUnassigning,
          disabled: isUnassigning
        }}
        onCancel={() => {
          if (!isUnassigning) {
            setUserToUnassign(null);
          }
        }}
        onConfirm={handleUnassign}
      />
    </>
  );
};

export default EstateUnitUsersPanel;
